import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
  Tooltip,
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { accountsAPI } from '../services/api';
import { toast } from 'react-toastify';
import { format } from 'date-fns';

interface PendingAccount {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  registeredAt: string;
  isActive: boolean;
}

const AdminPendingAccounts: React.FC = () => {
  const [accounts, setAccounts] = useState<PendingAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [currentAccount, setCurrentAccount] = useState<PendingAccount | null>(null);

  // Form state
  const [role, setRole] = useState('USER');
  const [approvalNotes, setApprovalNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');

  const loadPendingAccounts = async () => {
    setLoading(true);
    try {
      const response = await accountsAPI.getPending();
      const data = response.data?.accounts || response.data?.data || response.data;
      setAccounts(Array.isArray(data) ? data : []);
    } catch (error: any) {
      toast.error('Failed to load pending accounts');
      console.error(error);
      setAccounts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPendingAccounts();
  }, []);

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelected(new Set(accounts.map((a) => a.id)));
    } else {
      setSelected(new Set());
    }
  };

  const handleSelectOne = (id: string) => {
    const newSelected = new Set(selected);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelected(newSelected);
  };

  const handleApproveOne = (account: PendingAccount) => {
    setCurrentAccount(account);
    setRole('USER');
    setApprovalNotes('');
    setApproveDialogOpen(true);
  };

  const handleRejectOne = (account: PendingAccount) => {
    setCurrentAccount(account);
    setRejectionReason('');
    setRejectDialogOpen(true);
  };

  const handleApprove = async () => {
    if (!currentAccount) return;

    try {
      await accountsAPI.approve(currentAccount.id, {
        role,
        notes: approvalNotes || undefined,
      });
      toast.success(`Account approved for ${currentAccount.email}`);
      setApproveDialogOpen(false);
      setCurrentAccount(null);
      loadPendingAccounts();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to approve account');
      console.error(error);
    }
  };

  const handleReject = async () => {
    if (!currentAccount) return;

    if (!rejectionReason.trim()) {
      toast.error('Rejection reason is required');
      return;
    }

    try {
      await accountsAPI.reject(currentAccount.id, {
        reason: rejectionReason,
      });
      toast.success(`Account rejected for ${currentAccount.email}`);
      setRejectDialogOpen(false);
      setCurrentAccount(null);
      loadPendingAccounts();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to reject account');
      console.error(error);
    }
  };

  const handleBulkApprove = async () => {
    if (selected.size === 0) {
      toast.warning('No accounts selected');
      return;
    }

    if (!window.confirm(`Approve ${selected.size} account(s)?`)) return;

    try {
      await accountsAPI.bulkApprove({
        userIds: Array.from(selected),
        role: 'USER',
      });
      toast.success(`${selected.size} account(s) approved successfully`);
      setSelected(new Set());
      loadPendingAccounts();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to bulk approve accounts');
      console.error(error);
    }
  };

  const formatFullName = (account: PendingAccount) => {
    if (account.firstName || account.lastName) {
      return `${account.firstName || ''} ${account.lastName || ''}`.trim();
    }
    return account.email;
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight="bold">
          Pending Account Approvals
        </Typography>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadPendingAccounts}
            disabled={loading}
          >
            Refresh
          </Button>
          {selected.size > 0 && (
            <Button
              variant="contained"
              color="success"
              startIcon={<ApproveIcon />}
              onClick={handleBulkApprove}
            >
              Approve {selected.size} Selected
            </Button>
          )}
        </Stack>
      </Stack>

      {accounts.length === 0 && !loading ? (
        <Alert severity="success" icon={<ApproveIcon />}>
          No pending account approvals. All users have been reviewed!
        </Alert>
      ) : (
        <>
          <Alert severity="info" sx={{ mb: 2 }}>
            {accounts.length} account(s) awaiting approval. These users self-registered and need administrator approval before they can access the system.
          </Alert>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selected.size > 0 && selected.size === accounts.length}
                      indeterminate={selected.size > 0 && selected.size < accounts.length}
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Registered</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {accounts.map((account) => (
                  <TableRow key={account.id} selected={selected.has(account.id)}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selected.has(account.id)}
                        onChange={() => handleSelectOne(account.id)}
                      />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <PersonIcon color="action" />
                        <Typography fontWeight={500}>
                          {formatFullName(account)}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>{account.email}</TableCell>
                    <TableCell>{account.phone || '-'}</TableCell>
                    <TableCell>
                      {format(new Date(account.registeredAt), 'MMM d, yyyy h:mm a')}
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Tooltip title="Approve account">
                          <Button
                            size="small"
                            variant="outlined"
                            color="success"
                            startIcon={<ApproveIcon />}
                            onClick={() => handleApproveOne(account)}
                          >
                            Approve
                          </Button>
                        </Tooltip>
                        <Tooltip title="Reject account">
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            startIcon={<RejectIcon />}
                            onClick={() => handleRejectOne(account)}
                          >
                            Reject
                          </Button>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* Approve Dialog */}
      <Dialog open={approveDialogOpen} onClose={() => setApproveDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Approve Account</DialogTitle>
        <DialogContent>
          {currentAccount && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Alert severity="info">
                Approving <strong>{formatFullName(currentAccount)}</strong> ({currentAccount.email})
              </Alert>
              <TextField
                fullWidth
                select
                label="Assign Role"
                value={role}
                onChange={(e) => setRole(e.target.value)}
                helperText="Select the role for this user"
              >
                <MenuItem value="USER">User</MenuItem>
                <MenuItem value="ADMIN">Admin</MenuItem>
                <MenuItem value="SUPER_ADMIN">Super Admin</MenuItem>
              </TextField>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Approval Notes (Optional)"
                value={approvalNotes}
                onChange={(e) => setApprovalNotes(e.target.value)}
                helperText="Internal notes about this approval (optional)"
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="success" onClick={handleApprove} startIcon={<ApproveIcon />}>
            Approve
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Account</DialogTitle>
        <DialogContent>
          {currentAccount && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Alert severity="warning">
                Rejecting <strong>{formatFullName(currentAccount)}</strong> ({currentAccount.email})
              </Alert>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Rejection Reason"
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                required
                helperText="Provide a reason for rejection (will be sent to the user)"
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleReject} startIcon={<RejectIcon />}>
            Reject
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminPendingAccounts;
